import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { errMessage } from '../../lib/meetings';
import { istDay, nowIst } from '../../lib/format';

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const blankForm = (existing) => ({
  outcome: existing?.outcome || '',
  summary: existing?.summary || '',
  key_points: existing?.key_points || '',
  requirements: existing?.requirements || '',
  objections: existing?.objections || [],
  objection_details: existing?.objection_details || '',
  next_action: existing?.next_action || '',
  next_action_date: existing?.next_action_date || '',
  deal_value_discussed: existing?.deal_value_discussed ?? '',
  lost_reason: existing?.lost_reason || '',
  edit_reason: '',
});

const needsLostReason = (o) => o === 'Not Interested' || o === 'Disqualified';

const isLocked = (meeting, lockHours) => {
  const at = meeting?.outcome?.recorded_at;
  if (!at) return false;
  return nowIst().diff(istDay(at), 'hour') >= (lockHours || 48);
};

const ObjectionsPicker = ({ options, form, set }) => {
  const toggle = (o) => set('objections', form.objections.includes(o)
    ? form.objections.filter((x) => x !== o) : [...form.objections, o]);
  return (
    <Field label="Objections Raised">
      <div className="grid grid-cols-4 gap-1.5" data-testid="outcome-objections">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer rounded-md border px-2 py-1.5 hover:bg-muted/50">
            <Checkbox checked={form.objections.includes(o)} onCheckedChange={() => toggle(o)}
              data-testid={`outcome-objection-${o.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
            <span className="truncate">{o}</span>
          </label>
        ))}
      </div>
    </Field>
  );
};

const OutcomeSelectors = ({ meta, form, set }) => (
  <>
    <Field label="Outcome *">
      <Select value={form.outcome} onValueChange={(v) => set('outcome', v)}>
        <SelectTrigger data-testid="outcome-select"><SelectValue placeholder="How did it go?" /></SelectTrigger>
        <SelectContent>
          {meta.outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
    {needsLostReason(form.outcome) && (
      <Field label="Loss Reason (required)">
        <Select value={form.lost_reason} onValueChange={(v) => set('lost_reason', v)}>
          <SelectTrigger className={!form.lost_reason ? 'border-red-400' : ''} data-testid="outcome-lost-reason-select">
            <SelectValue placeholder="Why was it lost?" />
          </SelectTrigger>
          <SelectContent>
            {meta.lost_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    )}
  </>
);

const NextActionFields = ({ meta, form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Next Action">
      <Select value={form.next_action || '__none__'} onValueChange={(v) => set('next_action', v === '__none__' ? '' : v)}>
        <SelectTrigger data-testid="outcome-next-action-select"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">—</SelectItem>
          {meta.next_actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
    <Field label="Next Action Date (creates follow-up reminder)">
      <Input type="date" value={form.next_action_date} onChange={(e) => set('next_action_date', e.target.value)}
        data-testid="outcome-next-date-input" />
    </Field>
  </div>
);

/** Mandatory post-meeting outcome form — drives the exact lead/Kanban sync rules. */
export const OutcomeDialog = ({ open, onClose, meeting, onSaved }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(blankForm(null));
  const [saving, setSaving] = useState(false);
  const isEdit = !!meeting?.outcome;

  useEffect(() => {
    if (!open) return;
    api.get('/meetings/meta').then(({ data }) => setMeta(data)).catch(() => {});
    const base = blankForm(meeting?.outcome);
    // Pre-fill suggestions from the AI notetaker summary (user confirms on save)
    const t = meeting?.transcript;
    if (!meeting?.outcome && t?.status === 'Ready') {
      base.summary = base.summary || t.summary_overview || '';
      base.key_points = base.key_points || (t.key_points || []).join('\n');
    }
    setForm(base);
  }, [open, meeting]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!open || !meta || !meeting) return null;
  const locked = isLocked(meeting, meta.outcome_lock_hours);

  const save = async () => {
    if (!form.outcome) { toast.error('Select an outcome'); return; }
    if (needsLostReason(form.outcome) && form.outcome === 'Not Interested' && !form.lost_reason) {
      toast.error('Loss Reason is required for Not Interested'); return;
    }
    if (locked && !form.edit_reason.trim()) { toast.error('An edit reason is required for a locked outcome'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/outcome`, {
        ...form,
        deal_value_discussed: form.deal_value_discussed === '' ? null : Number(form.deal_value_discussed),
        next_action: form.next_action || null,
        next_action_date: form.next_action_date || null,
        lost_reason: form.lost_reason || null,
        edit_reason: form.edit_reason || null,
      });
      toast.success(form.outcome === 'Deal Closed'
        ? 'Deal Closed! Lead moved to Won — incentives updated 🎉'
        : 'Outcome recorded — lead synced');
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(errMessage(e, 'Failed to record outcome'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardCheck size={17} className="text-primary" /> {isEdit ? 'Edit Outcome' : 'Record Outcome'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {meeting.title} — the outcome updates the linked lead & Kanban board instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <OutcomeSelectors meta={meta} form={form} set={set} />
          <Field label="Meeting Summary">
            <Textarea rows={2} value={form.summary} onChange={(e) => set('summary', e.target.value)} data-testid="outcome-summary-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key Discussion Points">
              <Textarea rows={2} value={form.key_points} onChange={(e) => set('key_points', e.target.value)} data-testid="outcome-keypoints-input" />
            </Field>
            <Field label="Client Requirements / Pain Points">
              <Textarea rows={2} value={form.requirements} onChange={(e) => set('requirements', e.target.value)} data-testid="outcome-requirements-input" />
            </Field>
          </div>
          <ObjectionsPicker options={meta.objections} form={form} set={set} />
          {form.objections.length > 0 && (
            <Field label="Objection Details">
              <Input value={form.objection_details} onChange={(e) => set('objection_details', e.target.value)} data-testid="outcome-objection-details-input" />
            </Field>
          )}
          <NextActionFields meta={meta} form={form} set={set} />
          <Field label="Deal Value Discussed (₹, optional)">
            <Input type="number" min="0" value={form.deal_value_discussed}
              onChange={(e) => set('deal_value_discussed', e.target.value)} data-testid="outcome-deal-value-input" />
          </Field>
          {locked && (
            <Field label="Edit Reason (required — outcome is locked)">
              <Input value={form.edit_reason} onChange={(e) => set('edit_reason', e.target.value)}
                className="border-orange-300" data-testid="outcome-edit-reason-input" />
            </Field>
          )}
          <div className="flex gap-2">
            <Button className="flex-1" onClick={save} disabled={saving} data-testid="outcome-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ClipboardCheck size={14} className="mr-1.5" />}
              Save Outcome
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="outcome-close-btn">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
